import { createFileRoute } from '@tanstack/react-router'
import { CalendarIcon, HeartIcon, LogOut, MapPinIcon, Settings, User } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { ChoiceChip } from '@/components/ui/choice-chip'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { OptionTile } from '@/components/ui/option-tile'
import { PhotoCard } from '@/components/ui/photo-card'
import { ScrollableGallery } from '@/components/ui/scrollable-gallery'
import { SegmentedToggle } from '@/components/ui/segmented-toggle'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'

export const Route = createFileRoute('/ui')({
  component: UiShowcase,
})

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-4">
      <h2 className="text-sm font-medium tracking-wide text-muted-foreground uppercase">{title}</h2>
      <div className="flex flex-wrap items-start gap-4">{children}</div>
    </section>
  )
}

function UiShowcase() {
  const [agreed, setAgreed] = useState(true)
  const [toggleValue, setToggleValue] = useState<'option1' | 'option2'>('option1')
  const [selectedChips, setSelectedChips] = useState<string[]>(['chip1'])
  const [selectedCards, setSelectedCards] = useState<string[]>(['card1'])

  return (
    <div className="min-h-dvh bg-background text-foreground">
      <div className="page-stagger-root mx-auto max-w-4xl space-y-12 px-6 py-12">
        <header className="space-y-1">
          <h1 className="text-3xl font-semibold tracking-tight">Amoré UI</h1>
          <p className="text-sm text-muted-foreground">
            Component primitives — the shadcn/ui set, themed to the Amoré brand.
          </p>
        </header>

        <Section title="Button">
          <Button>Default</Button>
          <Button variant="secondary">Secondary</Button>
          <Button variant="outline">Outline</Button>
          <Button variant="ghost">Ghost</Button>
          <Button variant="link">Link</Button>
          <Button variant="destructive">Destructive</Button>
          <Button size="sm">Small</Button>
          <Button size="lg">Large</Button>
          <Button disabled>Disabled</Button>
        </Section>

        <Section title="Badge">
          <Badge>Default</Badge>
          <Badge variant="secondary">Secondary</Badge>
          <Badge variant="outline">Outline</Badge>
          <Badge variant="destructive">Destructive</Badge>
        </Section>

        <Section title="Input & Textarea">
          <div className="w-full max-w-sm space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" placeholder="you@example.com" />
          </div>
          <div className="w-full max-w-sm space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea id="notes" placeholder="A few words about the day…" />
          </div>
        </Section>

        <Section title="Checkbox">
          <div className="flex items-center gap-2">
            <Checkbox id="terms" checked={agreed} onCheckedChange={(v) => setAgreed(v === true)} />
            <Label htmlFor="terms">Send the couple a reminder</Label>
          </div>
        </Section>

        <Section title="Select">
          <Select>
            <SelectTrigger className="w-56">
              <SelectValue placeholder="Choose a venue type" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>Outdoor</SelectLabel>
                <SelectItem value="garden">Garden</SelectItem>
                <SelectItem value="beach">Beach</SelectItem>
                <SelectItem value="vineyard">Vineyard</SelectItem>
              </SelectGroup>
              <SelectGroup>
                <SelectLabel>Indoor</SelectLabel>
                <SelectItem value="ballroom">Ballroom</SelectItem>
                <SelectItem value="barn">Barn</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
        </Section>

        <Section title="Dropdown Menu">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">Open menu</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48">
              <DropdownMenuLabel>My account</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem>
                <User />
                Profile
              </DropdownMenuItem>
              <DropdownMenuItem>
                <Settings />
                Settings
                <DropdownMenuShortcut>⌘,</DropdownMenuShortcut>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem variant="destructive">
                <LogOut />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </Section>

        <Section title="Dialog">
          <Dialog>
            <DialogTrigger asChild>
              <Button>Invite collaborator</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Invite a collaborator</DialogTitle>
                <DialogDescription>
                  They'll get access to this workspace and every wedding inside it.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2">
                <Label htmlFor="invite-email">Email</Label>
                <Input id="invite-email" type="email" placeholder="planner@example.com" />
              </div>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>
                <Button>Send invite</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </Section>

        <Section title="Avatar">
          <Avatar>
            <AvatarImage src="https://github.com/shadcn.png" alt="@shadcn" />
            <AvatarFallback>CN</AvatarFallback>
          </Avatar>
          <Avatar>
            <AvatarFallback>AM</AvatarFallback>
          </Avatar>
        </Section>

        <Section title="Separator">
          <div className="flex h-5 items-center gap-3 text-sm">
            <span>Guests</span>
            <Separator orientation="vertical" />
            <span>Budget</span>
            <Separator orientation="vertical" />
            <span>Timeline</span>
          </div>
        </Section>

        <Section title="Skeleton">
          <div className="flex items-center gap-4">
            <Skeleton className="size-12 rounded-full" />
            <div className="space-y-2">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-4 w-32" />
            </div>
          </div>
        </Section>

        <Section title="Card">
          <Card className="w-full max-w-sm">
            <CardHeader>
              <CardTitle>Autumn Vineyard Wedding</CardTitle>
              <CardDescription>Sat, Oct 18 2026 · 120 guests</CardDescription>
              <CardAction>
                <Badge variant="secondary">Planning</Badge>
              </CardAction>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              Ceremony at 4pm, reception under the pergola. Vendor contracts pending.
            </CardContent>
            <CardFooter className="justify-end gap-2">
              <Button variant="outline" size="sm">
                View
              </Button>
              <Button size="sm">Open</Button>
            </CardFooter>
          </Card>
        </Section>

        <Section title="Field">
          <div className="w-full max-w-sm">
            <Field label="Bride's name" id="bride" placeholder="Enter name" />
          </div>
          <div className="w-full max-w-sm">
            <Field label="Groom's name" id="groom" placeholder="Enter name" optional />
          </div>
        </Section>

        <Section title="Choice Chip">
          <ChoiceChip
            selected={selectedChips.includes('chip1')}
            onClick={() =>
              setSelectedChips((prev) =>
                prev.includes('chip1') ? prev.filter((c) => c !== 'chip1') : [...prev, 'chip1'],
              )
            }
          >
            Garden
          </ChoiceChip>
          <ChoiceChip
            selected={selectedChips.includes('chip2')}
            onClick={() =>
              setSelectedChips((prev) =>
                prev.includes('chip2') ? prev.filter((c) => c !== 'chip2') : [...prev, 'chip2'],
              )
            }
          >
            Beach
          </ChoiceChip>
          <ChoiceChip
            selected={selectedChips.includes('chip3')}
            onClick={() =>
              setSelectedChips((prev) =>
                prev.includes('chip3') ? prev.filter((c) => c !== 'chip3') : [...prev, 'chip3'],
              )
            }
          >
            Vineyard
          </ChoiceChip>
        </Section>

        <Section title="Segmented Toggle">
          <SegmentedToggle
            options={[
              { value: 'option1' as const, label: 'Date known' },
              { value: 'option2' as const, label: 'Still searching' },
            ]}
            value={toggleValue}
            onValueChange={setToggleValue}
            className="w-full max-w-sm"
          />
        </Section>

        <Section title="Option Tile">
          <OptionTile
            icon={CalendarIcon}
            label="Spring"
            selected={selectedChips.includes('spring')}
            onClick={() =>
              setSelectedChips((prev) =>
                prev.includes('spring') ? prev.filter((c) => c !== 'spring') : [...prev, 'spring'],
              )
            }
            className="w-32"
          />
          <OptionTile
            icon={MapPinIcon}
            label="Outdoor"
            selected={selectedChips.includes('outdoor')}
            onClick={() =>
              setSelectedChips((prev) =>
                prev.includes('outdoor')
                  ? prev.filter((c) => c !== 'outdoor')
                  : [...prev, 'outdoor'],
              )
            }
            className="w-32"
          />
          <OptionTile
            icon={HeartIcon}
            label="Romantic"
            selected={selectedChips.includes('romantic')}
            onClick={() =>
              setSelectedChips((prev) =>
                prev.includes('romantic')
                  ? prev.filter((c) => c !== 'romantic')
                  : [...prev, 'romantic'],
              )
            }
            className="w-32"
          />
        </Section>

        <Section title="Photo Card">
          <PhotoCard
            image="https://images.unsplash.com/photo-1519741497674-611481863552?w=400"
            title="Castle"
            description="Historic elegance"
            selected={selectedCards.includes('card1')}
            onClick={() =>
              setSelectedCards((prev) =>
                prev.includes('card1') ? prev.filter((c) => c !== 'card1') : [...prev, 'card1'],
              )
            }
            className="w-60"
          />
          <PhotoCard
            image="https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?w=400"
            title="Barn"
            description="Rustic charm"
            selected={selectedCards.includes('card2')}
            onClick={() =>
              setSelectedCards((prev) =>
                prev.includes('card2') ? prev.filter((c) => c !== 'card2') : [...prev, 'card2'],
              )
            }
            className="w-60"
          />
        </Section>

        <Section title="Scrollable Gallery">
          <div className="w-full">
            <ScrollableGallery
              items={[
                <PhotoCard
                  key="castle"
                  image="https://images.unsplash.com/photo-1519741497674-611481863552?w=400"
                  title="Castle"
                  description="Historic elegance"
                  selected={false}
                />,
                <PhotoCard
                  key="barn"
                  image="https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?w=400"
                  title="Barn"
                  description="Rustic charm"
                  selected={false}
                />,
                <PhotoCard
                  key="hotel"
                  image="https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=400"
                  title="Hotel"
                  description="Modern comfort"
                  selected={false}
                />,
                <PhotoCard
                  key="garden"
                  image="https://images.unsplash.com/photo-1522673607200-164d1b6ce486?w=400"
                  title="Garden"
                  description="Natural beauty"
                  selected={false}
                />,
              ]}
              rows={1}
              ariaLabel="Venue options"
            />
          </div>
        </Section>
      </div>
    </div>
  )
}
